import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Meeting } from '../_models/meeting.model';
import { HttpUtilsService, QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { environment } from 'src/environments/environment';
import { select, Store } from '@ngrx/store';
import { AppState } from '../../reducers';
import { currentAuthToken, currentUser } from '../../auth/_selectors/auth.selectors';

@Injectable({ providedIn: 'root' })
export class MeetingService {
  private authToken;
  private companyId;

  constructor(
    private httpClient: HttpClient,
    private store: Store<AppState>,
    private httpUtils: HttpUtilsService,
  ) {
    this.store.pipe(select(currentAuthToken)).subscribe((res) => (this.authToken = res));
    this.store.pipe(select(currentUser)).subscribe((res) => (this.companyId = res.companyID));
  }

  uploadFiles(url, file): Observable<any> {
    return this.httpClient.put(url, file, {
      observe: 'events',
      reportProgress: true,
    });
  }

  sendFiles(id, uuids): Observable<any> {
    const url =
      environment.TIMELINE_BASE +
      '/companies/' +
      this.companyId +
      '/communication/' +
      id +
      '/attachments';
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      uuids,
    };
    return this.httpClient.post<any>(url, body, { headers: httpHeaders });
  }

  createMeeting(request, _meeting: Meeting) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      sender_name: request.body.sender_name,
      subject: _meeting.subject,
      from_time: _meeting.from_time,
      body: _meeting.body,
      location: _meeting.location,
      receivers: _meeting.receivers,
      attachment_names: request.body.attachment_names,
      is_completed: _meeting.is_completed,
      is_important: _meeting.is_important,
      related_to_list: _meeting.related_to_list,
      tags: _meeting.tags,
      drug_id: _meeting.drug_id,
      duration: _meeting.duration,
      call_organizer: _meeting.call_organizer,
      sales_call_type: _meeting.sales_call_type,
    };
    const params = new HttpParams({ fromString: 'communication_type=MEETING' });
    return this.httpClient.post<Meeting>(
      environment.TIMELINE_BASE + '/companies/' + this.companyId + '/communication',
      body,
      {
        headers: httpHeaders,
        observe: 'response',
        params,
      },
    );
  }

  deleteMeeting(id?: number) {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    return this.httpClient.delete<any>(
      environment.CORE_BASE + '/companies/' + this.companyId + '/meetings/' + id,
      { headers: httpHeaders },
    );
  }
  updateStatusForMeetings(ids?: Meeting[], isActive?: number) {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.set('Content-Type', 'application/json');
    httpHeaders.set('access-control-allow-methods', 'POST');
    httpHeaders = httpHeaders.set('Authorization', 'Bearer ' + this.authToken);
    const idsForUpdate: any[] = [];
    // tslint:disable-next-line:prefer-for-of
    for (let i = 0; i < ids.length; i++) {
      idsForUpdate.push(ids[i].id);
    }
    const body = {
      ids: idsForUpdate,
      isActive,
    };
    return this.httpClient.put<any>(environment.USER_BASE + '/meetingstatusupdate', body, {
      headers: httpHeaders,
    });
  }

  updateMeeting(request, _meeting: Meeting): Observable<any> {
    const httpHeaders = this.httpUtils.getHTTPHeaders(this.authToken);
    const body = {
      sender_name: request.body.sender_name,
      subject: _meeting.subject,
      from_time: _meeting.from_time,
      body: _meeting.body,
      location: _meeting.location,
      receivers: _meeting.receivers,
      attachments: request.body.attachment_names,
      is_completed: _meeting.is_completed,
      is_important: _meeting.is_important,
      related_to_list: _meeting.related_to_list,
      tags: _meeting.tags,
      drug_id: _meeting.drug_id,
      duration: _meeting.duration,
      call_organizer: _meeting.call_organizer,
      sales_call_type: _meeting.sales_call_type,
    };
    return this.httpClient.put<Meeting>(
      environment.TIMELINE_BASE +
        '/companies/' +
        this.companyId +
        '/communication/' +
        _meeting.id +
        '/update',
      body,
      {
        headers: httpHeaders,
        observe: 'response',
      },
    );
  }

  findMeetings(queryParams: QueryParamsModel): Observable<any> {
    const pageSize = queryParams.pageSize;
    const pageIndex = queryParams.pageNumber;
    const organizer_id = queryParams.filter.organizer_id;
    const entity_id = queryParams.filter.entity_id;
    const is_completed = queryParams.filter.is_completed;
    const to_time = queryParams.filter.to_time ? '&to_time=' + queryParams.filter.to_time : '';
    const from_time = queryParams.filter.from_time
      ? '&from_time=' + queryParams.filter.from_time
      : '';
    const sort = queryParams.sortField.replace(/([-_]\w)/g, (g) => g[1].toUpperCase());
    const headers = this.httpUtils.getHTTPHeaders(this.authToken);
    const params = new HttpParams({
      fromString:
        'communication_type=MEETING&search_term=' +
        queryParams.filter.searchData +
        '&sort=' +
        sort +
        `,${queryParams.sortOrder}` +
        '&page=' +
        pageIndex +
        '&size=' +
        pageSize +
        '&organizer_id=' +
        organizer_id +
        '&entity_id=' +
        entity_id +
        from_time +
        to_time +
        '&is_completed=' +
        is_completed,
    });
    return this.httpClient.get<QueryResultsModel>(
      environment.TIMELINE_BASE + '/companies/' + this.companyId + '/communication',
      {
        headers,
        params,
      },
    );
  }
}
